import type { PreviewData, PreviewNode, PreviewEdge, PreviewSection } from './types';

const CANVAS_PADDING = 40;
const BORDER_RADIUS = 8;
const STEP_BORDER_RADIUS = 6;
const ARROW_SIZE = 6;
const EDGE_STROKE_WIDTH = 2;

interface RenderOptions {
  backgroundColor?: string;
}

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const renderSections = (
  ctx: CanvasRenderingContext2D,
  sections: PreviewSection[],
  scale: number,
  offsetX: number,
  offsetY: number,
): void => {
  for (const section of sections) {
    const x = section.x * scale + offsetX;
    const y = section.y * scale + offsetY;
    const width = section.width * scale;
    const height = section.height * scale;

    ctx.fillStyle = section.color;
    drawRoundedRect(ctx, x, y, width, height, BORDER_RADIUS * scale);
    ctx.fill();
  }
};

const renderGroups = (
  ctx: CanvasRenderingContext2D,
  nodes: PreviewNode[],
  scale: number,
  offsetX: number,
  offsetY: number,
): void => {
  const groups = nodes.filter((n) => n.type === 'group');

  for (const group of groups) {
    const x = group.x * scale + offsetX;
    const y = group.y * scale + offsetY;
    const width = group.width * scale;
    const height = group.height * scale;

    ctx.fillStyle = group.color;
    drawRoundedRect(ctx, x, y, width, height, BORDER_RADIUS * scale);
    ctx.fill();
  }
};

const renderSteps = (
  ctx: CanvasRenderingContext2D,
  nodes: PreviewNode[],
  scale: number,
  offsetX: number,
  offsetY: number,
): void => {
  const steps = nodes.filter((n) => n.type === 'step');

  for (const step of steps) {
    const x = step.x * scale + offsetX;
    const y = step.y * scale + offsetY;
    const width = step.width * scale;
    const height = step.height * scale;

    ctx.fillStyle = step.color;
    drawRoundedRect(ctx, x, y, width, height, STEP_BORDER_RADIUS * scale);
    ctx.fill();
  }
};

const renderStandaloneNodes = (
  ctx: CanvasRenderingContext2D,
  nodes: PreviewNode[],
  scale: number,
  offsetX: number,
  offsetY: number,
): void => {
  const standalones = nodes.filter((n) => n.type === 'standalone');

  for (const standalone of standalones) {
    const x = standalone.x * scale + offsetX;
    const y = standalone.y * scale + offsetY;
    const width = standalone.width * scale;
    const height = standalone.height * scale;

    ctx.fillStyle = standalone.color;
    drawRoundedRect(ctx, x, y, width, height, 4 * scale);
    ctx.fill();

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1 * scale;
    drawRoundedRect(ctx, x, y, width, height, 4 * scale);
    ctx.stroke();

    const hPadding = 12 * scale;
    const maxTextWidth = width - hPadding * 2;
    const h4FontSize = 16 * scale;
    const captionFontSize = 12 * scale;
    const lineGap = 2 * scale;
    const totalTextHeight = h4FontSize + lineGap + captionFontSize;
    const textStartY = y + (height - totalTextHeight) / 2;

    if (standalone.label) {
      ctx.font = `600 ${h4FontSize}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = '#2A2D30';
      ctx.textBaseline = 'top';

      let labelText = standalone.label;
      let textWidth = ctx.measureText(labelText).width;
      if (textWidth > maxTextWidth) {
        while (textWidth > maxTextWidth && labelText.length > 3) {
          labelText = labelText.slice(0, -1);
          textWidth = ctx.measureText(labelText + '...').width;
        }
        labelText = labelText + '...';
      }
      ctx.fillText(labelText, x + hPadding, textStartY);
    }

    if (standalone.sublabel) {
      ctx.font = `400 ${captionFontSize}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = '#595966';
      ctx.textBaseline = 'top';

      let sublabelText = standalone.sublabel;
      let textWidth = ctx.measureText(sublabelText).width;
      if (textWidth > maxTextWidth) {
        while (textWidth > maxTextWidth && sublabelText.length > 3) {
          sublabelText = sublabelText.slice(0, -1);
          textWidth = ctx.measureText(sublabelText + '...').width;
        }
        sublabelText = sublabelText + '...';
      }

      const sublabelY = textStartY + h4FontSize + lineGap;
      ctx.fillText(sublabelText, x + hPadding, sublabelY);
    }
  }
};

const getNodeCenter = (
  nodeId: string,
  nodes: PreviewNode[],
  scale: number,
  offsetX: number,
  offsetY: number,
): { x: number; y: number } | null => {
  const isGroupLabel = nodeId.startsWith('label-');
  const actualId = isGroupLabel ? nodeId.replace('label-', '') : nodeId;

  const node = nodes.find((n) => n.id === actualId);
  if (!node) return null;

  const x = node.x * scale + offsetX;
  const y = node.y * scale + offsetY;
  const width = node.width * scale;
  const height = node.height * scale;

  // Group labels connect to the top-center of the group
  if (isGroupLabel) {
    return { x: x + width / 2, y: y };
  }
  return { x: x + width / 2, y: y + height / 2 };
};

const drawArrow = (
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  color: string,
  scale: number,
): void => {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const arrowSize = ARROW_SIZE * scale;

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - arrowSize * Math.cos(angle - Math.PI / 6),
    toY - arrowSize * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    toX - arrowSize * Math.cos(angle + Math.PI / 6),
    toY - arrowSize * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
};

const getBezierControlPoints = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): { cp1x: number; cp1y: number; cp2x: number; cp2y: number } => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const curvature = 0.25;

  // Curve direction based on whether the line is more horizontal or vertical
  if (Math.abs(dx) > Math.abs(dy)) {
    return {
      cp1x: fromX + dx * curvature,
      cp1y: fromY,
      cp2x: toX - dx * curvature,
      cp2y: toY,
    };
  } else {
    return {
      cp1x: fromX,
      cp1y: fromY + dy * curvature,
      cp2x: toX,
      cp2y: toY - dy * curvature,
    };
  }
};

const renderEdges = (
  ctx: CanvasRenderingContext2D,
  edges: PreviewEdge[],
  nodes: PreviewNode[],
  scale: number,
  offsetX: number,
  offsetY: number,
): void => {
  ctx.lineWidth = EDGE_STROKE_WIDTH * scale;
  ctx.lineCap = 'round';

  for (const edge of edges) {
    const sourceCenter = getNodeCenter(edge.sourceId, nodes, scale, offsetX, offsetY);
    const targetCenter = getNodeCenter(edge.targetId, nodes, scale, offsetX, offsetY);

    if (!sourceCenter || !targetCenter) continue;

    const gradient = ctx.createLinearGradient(
      sourceCenter.x,
      sourceCenter.y,
      targetCenter.x,
      targetCenter.y,
    );
    gradient.addColorStop(0, edge.sourceColor);
    gradient.addColorStop(1, edge.targetColor);

    const { cp1x, cp1y, cp2x, cp2y } = getBezierControlPoints(
      sourceCenter.x,
      sourceCenter.y,
      targetCenter.x,
      targetCenter.y,
    );

    ctx.strokeStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(sourceCenter.x, sourceCenter.y);
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, targetCenter.x, targetCenter.y);
    ctx.stroke();

    drawArrow(ctx, cp2x, cp2y, targetCenter.x, targetCenter.y, edge.targetColor, scale);

    // Draw source bullet point
    const bulletRadius = 3 * scale;
    ctx.fillStyle = edge.sourceColor;
    ctx.beginPath();
    ctx.arc(sourceCenter.x, sourceCenter.y, bulletRadius, 0, Math.PI * 2);
    ctx.fill();
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
};

export const renderPreviewToBlob = async (
  data: PreviewData,
  options: RenderOptions = {},
): Promise<Blob | null> => {
  const { backgroundColor = '#FFFFFF' } = options;

  if (data.nodes.length === 0 && data.sections.length === 0) {
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, 100, 100);
    }
    return canvasToBlob(canvas);
  }

  const canvasWidth = Math.ceil(data.boundingBox.width + CANVAS_PADDING * 2);
  const canvasHeight = Math.ceil(data.boundingBox.height + CANVAS_PADDING * 2);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const scale = 1;
  const offsetX = CANVAS_PADDING - data.boundingBox.minX;
  const offsetY = CANVAS_PADDING - data.boundingBox.minY;

  renderSections(ctx, data.sections, scale, offsetX, offsetY);
  renderGroups(ctx, data.nodes, scale, offsetX, offsetY);
  renderEdges(ctx, data.edges, data.nodes, scale, offsetX, offsetY);
  renderSteps(ctx, data.nodes, scale, offsetX, offsetY);
  renderStandaloneNodes(ctx, data.nodes, scale, offsetX, offsetY);

  return canvasToBlob(canvas);
};

/** @deprecated Use renderPreviewToBlob for better storage efficiency */
export const renderPreviewToCanvas = (data: PreviewData, options: RenderOptions = {}): string => {
  const { backgroundColor = '#FFFFFF' } = options;

  if (data.nodes.length === 0 && data.sections.length === 0) {
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, 100, 100);
    }
    return canvas.toDataURL('image/png');
  }

  const canvasWidth = Math.ceil(data.boundingBox.width + CANVAS_PADDING * 2);
  const canvasHeight = Math.ceil(data.boundingBox.height + CANVAS_PADDING * 2);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const scale = 1;
  const offsetX = CANVAS_PADDING - data.boundingBox.minX;
  const offsetY = CANVAS_PADDING - data.boundingBox.minY;

  renderSections(ctx, data.sections, scale, offsetX, offsetY);
  renderGroups(ctx, data.nodes, scale, offsetX, offsetY);
  renderEdges(ctx, data.edges, data.nodes, scale, offsetX, offsetY);
  renderSteps(ctx, data.nodes, scale, offsetX, offsetY);
  renderStandaloneNodes(ctx, data.nodes, scale, offsetX, offsetY);

  return canvas.toDataURL('image/png');
};

export const renderPreviewToCanvasWithSize = (
  data: PreviewData,
  width: number,
  height: number,
  options: RenderOptions = {},
): string => {
  const { backgroundColor = '#FFFFFF' } = options;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);

  if (data.nodes.length === 0 && data.sections.length === 0) {
    return canvas.toDataURL('image/png');
  }

  const canvasPadding = 20;
  const availableWidth = width - canvasPadding * 2;
  const availableHeight = height - canvasPadding * 2;

  const scaleX = availableWidth / data.boundingBox.width;
  const scaleY = availableHeight / data.boundingBox.height;
  const scale = Math.min(scaleX, scaleY, 1);

  const contentWidth = data.boundingBox.width * scale;
  const contentHeight = data.boundingBox.height * scale;
  const offsetX = (width - contentWidth) / 2 - data.boundingBox.minX * scale;
  const offsetY = (height - contentHeight) / 2 - data.boundingBox.minY * scale;

  renderSections(ctx, data.sections, scale, offsetX, offsetY);
  renderGroups(ctx, data.nodes, scale, offsetX, offsetY);
  renderEdges(ctx, data.edges, data.nodes, scale, offsetX, offsetY);
  renderSteps(ctx, data.nodes, scale, offsetX, offsetY);
  renderStandaloneNodes(ctx, data.nodes, scale, offsetX, offsetY);

  return canvas.toDataURL('image/png');
};

// Renders at a fixed scale (1 = actual size, 0.5 = half size) for detailed comparison
export const renderPreviewAtScale = (
  data: PreviewData,
  fixedScale: number = 1,
  options: RenderOptions = {},
): string => {
  const { backgroundColor = '#FFFFFF' } = options;

  const padding = 40;
  const canvasWidth = Math.ceil(data.boundingBox.width * fixedScale + padding * 2);
  const canvasHeight = Math.ceil(data.boundingBox.height * fixedScale + padding * 2);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  if (data.nodes.length === 0 && data.sections.length === 0) {
    return canvas.toDataURL('image/png');
  }

  const offsetX = padding - data.boundingBox.minX * fixedScale;
  const offsetY = padding - data.boundingBox.minY * fixedScale;

  renderSections(ctx, data.sections, fixedScale, offsetX, offsetY);
  renderGroups(ctx, data.nodes, fixedScale, offsetX, offsetY);
  renderEdges(ctx, data.edges, data.nodes, fixedScale, offsetX, offsetY);
  renderSteps(ctx, data.nodes, fixedScale, offsetX, offsetY);
  renderStandaloneNodes(ctx, data.nodes, fixedScale, offsetX, offsetY);

  return canvas.toDataURL('image/png');
};
